import type { ApiError } from '@paplats/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Läser CSRF-token från session-cookie (sätts av backend vid auth) */
function getCsrfToken(): string {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrf_token='))
      ?.split('=')[1] ?? ''
  );
}

export class ApiResponseError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.error);
    this.name = 'ApiResponseError';
  }
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
}

async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, params, headers: extraHeaders, ...rest } = options;

  // Bygg URL med query-params
  const url = new URL(BASE_URL + path, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders as Record<string, string>),
  };

  // Lägg till CSRF-token på mutationer
  const method = (rest.method ?? 'GET').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  }

  const response = await fetch(url.toString(), {
    ...rest,
    method,
    credentials: 'include', // Skicka httpOnly session-cookie
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorBody: ApiError;
    try {
      errorBody = (await response.json()) as ApiError;
    } catch {
      errorBody = { error: response.statusText, code: 'UNKNOWN' };
    }
    throw new ApiResponseError(response.status, errorBody);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ============================================================
// Auth
// ============================================================

export const authApi = {
  login: (email: string, password: string) =>
    apiFetch<{ admin: { id: number; name: string; email: string; role: string } }>(
      '/admin/auth/login',
      { method: 'POST', body: { email, password } }
    ),

  requestOtp: (email: string) =>
    apiFetch<{ message: string }>('/admin/auth/otp/request', {
      method: 'POST',
      body: { email },
    }),

  verifyOtp: (email: string, otp: string) =>
    apiFetch<{ admin: { id: number; name: string; email: string; role: string } }>(
      '/admin/auth/otp/verify',
      { method: 'POST', body: { email, otp } }
    ),

  logout: () => apiFetch<{ message: string }>('/admin/auth/logout', { method: 'POST' }),
};

// ============================================================
// Uppdrag
// ============================================================

export const assignmentsApi = {
  list: (params?: { status?: string }) =>
    apiFetch<{ data: unknown[] }>('/admin/assignments', { params }),

  get: (id: number) => apiFetch<{ data: unknown }>(`/admin/assignments/${id}`),

  create: (body: unknown) =>
    apiFetch<{ data: unknown }>('/admin/assignments', { method: 'POST', body }),

  update: (id: number, body: unknown) =>
    apiFetch<{ data: unknown }>(`/admin/assignments/${id}`, { method: 'PUT', body }),

  delete: (id: number) =>
    apiFetch<void>(`/admin/assignments/${id}`, { method: 'DELETE' }),

  duplicate: (id: number) =>
    apiFetch<{ data: unknown }>(`/admin/assignments/${id}/duplicate`, { method: 'POST' }),
};

// ============================================================
// Platser
// ============================================================

export const placesApi = {
  list: (assignmentId: number) =>
    apiFetch<{ data: unknown[] }>(`/admin/assignments/${assignmentId}/places`),

  get: (id: number) => apiFetch<{ data: unknown }>(`/admin/places/${id}`),

  create: (assignmentId: number, body: unknown) =>
    apiFetch<{ data: unknown }>(`/admin/assignments/${assignmentId}/places`, {
      method: 'POST',
      body,
    }),

  update: (id: number, body: unknown) =>
    apiFetch<{ data: unknown }>(`/admin/places/${id}`, { method: 'PUT', body }),

  delete: (id: number) =>
    apiFetch<void>(`/admin/places/${id}`, { method: 'DELETE' }),
};
